import { useEffect, useState, useCallback } from "react";
import { api, GBP } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PaymentBadge, StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CurriculumManager } from "@/components/CurriculumManager";
import { StoreManager } from "@/components/StoreManager";
import { ContentManager } from "@/components/ContentManager";
import { EnquiriesManager } from "@/components/EnquiriesManager";
import { ServicesManager } from "@/components/ServicesManager";
import { PoundSterling, Users, BookOpen, Award, Plus, ClipboardCheck, BadgeCheck, Star, Sparkles, X, Calendar, TrendingUp, Image as ImageIcon } from "lucide-react";

const fmtDay = (d) => new Date(d + "T00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
import { EmailAdmin } from "@/pages/provider/EmailAdmin";

const inputCls = "w-full px-3 py-2 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm";
const PAYMENT_STATUSES = ["deposit_paid", "part_paid", "paid_in_full", "sponsored", "payment_plan_active", "overdue", "refunded"];

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-[var(--line)] bg-white p-5 flex items-center justify-between" data-testid={`pstat-${label}`}>
      <div><div className="font-display font-extrabold text-2xl">{value}</div><div className="text-sm text-[var(--ink-2)]">{label}</div></div>
      <Icon className="text-[var(--accent)]" size={24} />
    </div>
  );
}

function CreateCourseDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", category: "AI & Digital Skills", summary: "", level: "Beginner", delivery: "Online", format: "Intensive", location: "Online", duration: "", price: "", deposit_amount: "", capacity: "", start_date: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      const body = { ...f, price: parseFloat(f.price || 0), deposit_amount: parseFloat(f.deposit_amount || 0), capacity: parseInt(f.capacity || 0, 10),
        timetable: f.start_date ? [{ label: "Day 1", date: f.start_date, time: "09:30 - 16:30" }] : [] };
      await api.post("/courses", body);
      toast.success("Course created!"); setOpen(false); onCreated();
    } catch (e) { toast.error("Could not create course."); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="create-course-button" className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-4 py-2.5 hover:bg-[var(--accent-hover)] text-sm"><Plus size={16} /> New course</button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Create course</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="cc-title" placeholder="Course title" value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} />
          <input data-testid="cc-category" placeholder="Category" value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls} />
          <textarea data-testid="cc-summary" placeholder="Short summary" value={f.summary} onChange={(e) => set("summary", e.target.value)} rows={2} className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="cc-delivery" value={f.delivery} onChange={(e) => set("delivery", e.target.value)} className={inputCls}>{["Online", "In-person", "Hybrid"].map((o) => <option key={o}>{o}</option>)}</select>
            <select data-testid="cc-level" value={f.level} onChange={(e) => set("level", e.target.value)} className={inputCls}>{["Beginner", "Intermediate", "Advanced"].map((o) => <option key={o}>{o}</option>)}</select>
            <select data-testid="cc-format" value={f.format} onChange={(e) => set("format", e.target.value)} className={inputCls}>{["Intensive", "Weekend", "Evening", "Weekday"].map((o) => <option key={o}>{o}</option>)}</select>
            <input data-testid="cc-location" placeholder="Location" value={f.location} onChange={(e) => set("location", e.target.value)} className={inputCls} />
            <input data-testid="cc-duration" placeholder="Duration (e.g. 2 days)" value={f.duration} onChange={(e) => set("duration", e.target.value)} className={inputCls} />
            <input data-testid="cc-start" type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} />
            <input data-testid="cc-price" type="number" placeholder="Price £" value={f.price} onChange={(e) => set("price", e.target.value)} className={inputCls} />
            <input data-testid="cc-deposit" type="number" placeholder="Deposit £" value={f.deposit_amount} onChange={(e) => set("deposit_amount", e.target.value)} className={inputCls} />
            <input data-testid="cc-capacity" type="number" placeholder="Capacity" value={f.capacity} onChange={(e) => set("capacity", e.target.value)} className={inputCls} />
          </div>
        </div>
        <DialogFooter><button onClick={save} data-testid="cc-save" className="bg-[var(--accent)] text-white font-semibold px-5 py-2.5 hover:bg-[var(--accent-hover)] w-full">Create course</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageEnrolmentDialog({ enrolment, onChange, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [pay, setPay] = useState({ method: "bank", amount: enrolment.deposit_amount || 0, payment_status: "deposit_paid", note: "" });
  const [att, setAtt] = useState({ session_label: "Day 1", status: "present", note: "" });
  const [testCourse, setTestCourse] = useState(false);
  const [certName, setCertName] = useState(enrolment.learner_name || "");
  const grantAccess = async () => {
    try {
      await api.post(`/provider/enrolments/${enrolment.id}/grant-access`, { is_test: testCourse });
      toast.success(testCourse ? "Free access granted (test — no certificate)" : "Free access granted"); onChange(); setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not grant access"); }
  };

  const setStatus = async (status) => { await api.patch(`/provider/enrolments/${enrolment.id}`, { status }); toast.success(`Marked ${status}`); onChange(); };
  const recordPayment = async () => {
    await api.post(`/provider/enrolments/${enrolment.id}/manual-payment`, { ...pay, amount: parseFloat(pay.amount || 0) });
    toast.success("Payment recorded"); onChange(); setOpen(false);
  };
  const markAttendance = async () => { await api.post("/provider/attendance", { enrolment_id: enrolment.id, ...att }); toast.success("Attendance marked"); onChange(); };
  const complete = async () => { await api.post(`/provider/enrolments/${enrolment.id}/complete`); toast.success("Certificate issued!"); onChange(); setOpen(false); };
  const saveCertName = async () => {
    if (!certName.trim()) { toast.error("Name cannot be empty"); return; }
    try {
      await api.patch(`/provider/certificates/${enrolment.certificate_number}`, { learner_name: certName.trim() });
      toast.success("Certificate name updated"); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update name"); }
  };
  const processRefund = async () => {
    if (!window.confirm(`Refund ${enrolment.learner_name} via Stripe? This returns their paid amount and cannot be undone.`)) return;
    try {
      const { data } = await api.post(`/provider/enrolments/${enrolment.id}/refund`);
      toast.success(`Refund processed (£${data.refunded_amount || 0}).`);
      if (data.errors?.length) toast.error("Some charges could not be refunded automatically — check Stripe.");
      onChange(); setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`manage-${enrolment.id}`} className="text-sm font-semibold text-[var(--accent)] hover:underline">Manage</button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{enrolment.learner_name}</DialogTitle></DialogHeader>
        <div className="text-sm text-[var(--ink-2)] -mt-2 mb-2">{enrolment.course_title} · {enrolment.learner_email}</div>

        <div className="space-y-5">
          <div>
            <p className="text-xs uppercase tracking-wide font-semibold text-[var(--ink-2)] mb-2">Application status</p>
            <div className="flex flex-wrap gap-2">
              {["approved", "enrolled", "waitlisted", "rejected"].map((s) => (
                <button key={s} onClick={() => setStatus(s)} data-testid={`set-status-${s}`} className="text-xs font-semibold border border-[var(--line)] px-3 py-1.5 hover:border-[var(--ink)] capitalize">{s}</button>
              ))}
            </div>
          </div>

          <div className="border-t border-[var(--line)] pt-4">
            <p className="text-xs uppercase tracking-wide font-semibold text-[var(--ink-2)] mb-2 flex items-center gap-1.5"><PoundSterling size={13} /> Record manual payment</p>
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="mp-method" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} className={inputCls}>{["bank", "cash", "sponsored", "invoice"].map((o) => <option key={o} value={o}>{o}</option>)}</select>
              <input data-testid="mp-amount" type="number" placeholder="Amount £" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className={inputCls} />
              <select data-testid="mp-status" value={pay.payment_status} onChange={(e) => setPay({ ...pay, payment_status: e.target.value })} className={`${inputCls} col-span-2`}>{PAYMENT_STATUSES.map((o) => <option key={o} value={o}>{o}</option>)}</select>
            </div>
            <button onClick={recordPayment} data-testid="mp-save" className="mt-2 w-full bg-[var(--ink)] text-white font-semibold py-2.5 text-sm hover:bg-[var(--accent)]">Record payment</button>
          </div>

          <div className="border-t border-[var(--line)] pt-4">
            <p className="text-xs uppercase tracking-wide font-semibold text-[var(--ink-2)] mb-2 flex items-center gap-1.5"><ClipboardCheck size={13} /> Mark attendance</p>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="att-session" placeholder="Session" value={att.session_label} onChange={(e) => setAtt({ ...att, session_label: e.target.value })} className={inputCls} />
              <select data-testid="att-status" value={att.status} onChange={(e) => setAtt({ ...att, status: e.target.value })} className={inputCls}>{["present", "late", "absent", "excused"].map((o) => <option key={o} value={o}>{o}</option>)}</select>
            </div>
            <button onClick={markAttendance} data-testid="att-save" className="mt-2 w-full border border-[var(--ink)] font-semibold py-2.5 text-sm hover:bg-[var(--ink)] hover:text-white">Save attendance</button>
          </div>

          <div className="border-t border-[var(--line)] pt-4">
            <button onClick={complete} data-testid="issue-certificate" className="w-full bg-[var(--accent)] text-white font-semibold py-3 text-sm hover:bg-[var(--accent-hover)] inline-flex items-center justify-center gap-2"><BadgeCheck size={16} /> {enrolment.certificate_number ? "View certificate" : "Mark complete & issue certificate"}</button>
            {enrolment.certificate_number && (
              <div className="mt-3" data-testid="cert-name-editor">
                <label className="text-xs uppercase tracking-wide font-semibold text-[var(--ink-2)] mb-1.5 block">Name on certificate</label>
                <div className="flex gap-2">
                  <input data-testid="cert-name-input" value={certName} onChange={(e) => setCertName(e.target.value)} className={inputCls} />
                  <button onClick={saveCertName} data-testid="cert-name-save" className="shrink-0 border border-[var(--ink)] font-semibold px-4 text-sm hover:bg-[var(--ink)] hover:text-white transition-colors">Update</button>
                </div>
                <p className="text-xs text-[var(--ink-2)] mt-1.5">Certificate: {enrolment.certificate_number}</p>
              </div>
            )}
          </div>

          {isAdmin && !["paid_in_full", "comped"].includes(enrolment.payment_status) && (
            <div className="border-t border-[var(--line)] pt-4" data-testid="grant-access-block">
              <p className="text-xs uppercase tracking-wide font-semibold text-[var(--ink-2)] mb-2">Grant free access (admin)</p>
              <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer"><input type="checkbox" checked={testCourse} onChange={(e) => setTestCourse(e.target.checked)} data-testid="grant-test" /> Test course — don't issue a certificate</label>
              <button onClick={grantAccess} data-testid="grant-access" className="w-full bg-[#1B5E20] text-white font-semibold py-2.5 text-sm hover:bg-[#154d1a]">Unlock free of charge</button>
            </div>
          )}

          {(enrolment.amount_paid || 0) > 0 && enrolment.payment_status !== "refunded" && (
            <div className="border-t border-[var(--line)] pt-4">
              {enrolment.refund_request?.status === "requested" && <p className="text-xs text-[#E65100] mb-2" data-testid="refund-pending-note">Learner has requested a refund.</p>}
              <button onClick={processRefund} data-testid="process-refund" className="w-full border border-[#B71C1C] text-[#B71C1C] font-semibold py-2.5 text-sm hover:bg-[#B71C1C] hover:text-white transition-colors">Process Stripe refund</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditCourseDialog({ course, onSaved }) {
  const [open, setOpen] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const toLines = (a) => (Array.isArray(a) ? a.join("\n") : (a || ""));
  const [f, setF] = useState(course);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const openEdit = () => {
    setF({ ...course,
      cohorts: (course.cohorts && course.cohorts.length)
        ? course.cohorts.map((c) => ({ ...c }))
        : (course.cohort_dates || []).map((d) => ({ id: d, date: d, time: "", location: "", capacity: "" })),
      snap_mondays: course.snap_mondays !== false,
      skills: toLines(course.skills), outcomes: toLines(course.outcomes),
      included: toLines(course.included), requirements: toLines(course.requirements) });
    setOpen(true);
  };
  const save = async () => {
    try {
      const lines = (s) => (typeof s === "string" ? s.split("\n").map((x) => x.trim()).filter(Boolean) : s);
      const body = { ...f,
        price: parseFloat(f.price || 0), deposit_amount: parseFloat(f.deposit_amount || 0),
        capacity: parseInt(f.capacity || 0, 10),
        cohorts: (f.cohorts || []).map((c) => ({ ...c, capacity: parseInt(c.capacity || 0, 10) })),
        snap_mondays: !!f.snap_mondays, cohort_dates: [],
        skills: lines(f.skills), outcomes: lines(f.outcomes), included: lines(f.included), requirements: lines(f.requirements) };
      await api.put(`/courses/${course.id}`, body);
      toast.success("Course updated"); setOpen(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const [nc, setNc] = useState({ date: "", time: "", location: "", capacity: "" });
  const [aiLoading, setAiLoading] = useState(false);
  const addCohort = () => {
    if (!nc.date) { toast.error("Pick a date"); return; }
    let date = nc.date;
    if (f.snap_mondays) {
      const d = new Date(date + "T00:00");
      const add = (8 - d.getDay()) % 7; // 0 if already Monday
      if (add) { d.setDate(d.getDate() + add); date = d.toISOString().slice(0, 10); toast.info("Adjusted to the Monday of that week"); }
    }
    if ((f.cohorts || []).some((c) => c.date === date)) { toast.info("That date is already added"); return; }
    const id = (window.crypto?.randomUUID?.() || String(Date.now()));
    setF((p) => ({ ...p, cohorts: [...(p.cohorts || []), { ...nc, date, id }].sort((a, b) => a.date.localeCompare(b.date)) }));
    setNc({ date: "", time: "", location: "", capacity: "" });
  };
  const removeCohort = (id) => setF((p) => ({ ...p, cohorts: (p.cohorts || []).filter((c) => c.id !== id) }));
  const generateAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/courses/${course.id}/ai-generate`);
      setF((p) => ({ ...p, who_for: data.who_for || p.who_for, skills: (data.skills || []).join("\n"), outcomes: (data.outcomes || []).join("\n") }));
      toast.success("AI copy generated — review and save");
    } catch (e) { toast.error(e.response?.data?.detail || "AI generation failed"); }
    finally { setAiLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button onClick={openEdit} data-testid={`edit-course-${course.id}`} className="text-xs font-semibold border border-[var(--line)] px-3 py-1.5 hover:border-[var(--ink)] w-full">Edit course</button>
      <DialogContent className="rounded-none max-h-[88vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">Edit: {course.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="ec-title" value={f.title || ""} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder="Title" />
          <textarea data-testid="ec-summary" value={f.summary || ""} onChange={(e) => set("summary", e.target.value)} rows={3} className={inputCls} placeholder="Summary / overview" />
          <div className="grid grid-cols-2 gap-3">
            <input value={f.category || ""} onChange={(e) => set("category", e.target.value)} className={inputCls} placeholder="Category" />
            <select value={f.level || "Beginner"} onChange={(e) => set("level", e.target.value)} className={inputCls}>{["Beginner", "Intermediate", "Advanced"].map((o) => <option key={o}>{o}</option>)}</select>
            <select value={f.delivery || "Online"} onChange={(e) => set("delivery", e.target.value)} className={inputCls}>{["Online", "In-person", "Hybrid"].map((o) => <option key={o}>{o}</option>)}</select>
            <select value={f.format || "Weekday"} onChange={(e) => set("format", e.target.value)} className={inputCls}>{["Intensive", "Weekend", "Evening", "Weekday"].map((o) => <option key={o}>{o}</option>)}</select>
            <input value={f.location || ""} onChange={(e) => set("location", e.target.value)} className={inputCls} placeholder="Location" />
            <input value={f.duration || ""} onChange={(e) => set("duration", e.target.value)} className={inputCls} placeholder="Duration" />
            <input type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className={inputCls} />
            <input type="number" value={f.price ?? ""} onChange={(e) => set("price", e.target.value)} className={inputCls} placeholder="Price £" />
            <input type="number" value={f.deposit_amount ?? ""} onChange={(e) => set("deposit_amount", e.target.value)} className={inputCls} placeholder="Deposit £" />
            <input type="number" value={f.capacity ?? ""} onChange={(e) => set("capacity", e.target.value)} className={inputCls} placeholder="Capacity" />
          </div>
          <div className="flex gap-2 items-center">
            <input data-testid="ec-image" value={f.image || ""} onChange={(e) => set("image", e.target.value)} className={inputCls} placeholder="Image URL" />
            <label data-testid="ec-image-upload" className="shrink-0 cursor-pointer text-xs font-semibold border border-[var(--line)] px-3 py-2 hover:bg-[var(--alt)] whitespace-nowrap">
              {imgUploading ? "Uploading…" : "Upload"}
              <input type="file" accept="image/*" className="hidden" disabled={imgUploading} onChange={async (e) => {
                const file = e.target.files?.[0]; if (!file) return;
                setImgUploading(true);
                try {
                  const fd = new FormData(); fd.append("file", file);
                  const { data } = await api.post("/media/image?folder=courses/", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  set("image", data.url); toast.success("Image uploaded");
                } catch (err) { toast.error("Upload failed. Check the image and try again."); }
                finally { setImgUploading(false); e.target.value = ""; }
              }} />
            </label>
          </div>
          <input data-testid="ec-video" value={f.video_url || ""} onChange={(e) => set("video_url", e.target.value)} className={inputCls} placeholder="Promo video URL (YouTube / Vimeo)" />

          <div className="grid grid-cols-1 gap-3 border border-[var(--line)] p-3">
            <label className="text-xs font-semibold text-[var(--ink-2)]">Tutor</label>
            <input data-testid="ec-tutor-name" value={f.tutor_name || ""} onChange={(e) => set("tutor_name", e.target.value)} className={inputCls} placeholder="Tutor name" />
            <textarea data-testid="ec-tutor-bio" value={f.tutor_bio || ""} onChange={(e) => set("tutor_bio", e.target.value)} rows={2} className={inputCls} placeholder="Tutor bio (optional)" />
          </div>

          <div className="border border-[var(--line)] p-3" data-testid="ec-gallery">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-[var(--ink-2)] flex items-center gap-1.5"><ImageIcon size={13} /> Promotional images</label>
              <label data-testid="ec-gallery-upload" className="cursor-pointer text-xs font-semibold border border-[var(--line)] px-3 py-1.5 hover:bg-[var(--alt)] whitespace-nowrap">
                {galleryUploading ? "Uploading…" : "Add images"}
                <input type="file" accept="image/*" multiple className="hidden" disabled={galleryUploading} onChange={async (e) => {
                  const files = Array.from(e.target.files || []); if (!files.length) return;
                  setGalleryUploading(true);
                  try {
                    const urls = [];
                    for (const file of files) {
                      const fd = new FormData(); fd.append("file", file);
                      const { data } = await api.post("/media/image?folder=courses/", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      urls.push(data.url);
                    }
                    setF((p) => ({ ...p, gallery: [...(p.gallery || []), ...urls] }));
                    toast.success(`${urls.length} image${urls.length === 1 ? "" : "s"} added`);
                  } catch (err) { toast.error("Some images failed to upload. Please try again."); }
                  finally { setGalleryUploading(false); e.target.value = ""; }
                }} />
              </label>
            </div>
            <p className="text-xs text-[var(--ink-2)] mb-2">Extra photos shown in a gallery on the course page (you can add several).</p>
            {(f.gallery || []).length === 0 ? (
              <p className="text-xs text-[var(--ink-2)] italic">No promotional images yet.</p>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {(f.gallery || []).map((url, i) => (
                  <div key={url + i} className="relative group" data-testid={`ec-gallery-item-${i}`}>
                    <img src={url} alt={`Promo ${i + 1}`} className="w-full aspect-square object-cover border border-[var(--line)]" />
                    <button type="button" data-testid={`ec-gallery-remove-${i}`}
                      onClick={() => setF((p) => ({ ...p, gallery: (p.gallery || []).filter((_, idx) => idx !== i) }))}
                      className="absolute top-1 right-1 bg-black/70 text-white w-6 h-6 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer border border-[var(--line)] p-3" data-testid="ec-cpd-accredited">
            <input type="checkbox" className="mt-0.5 accent-[var(--accent)]"
              checked={f.cpd_accredited == null ? (f.delivery === "Online") : !!f.cpd_accredited}
              onChange={(e) => set("cpd_accredited", e.target.checked)} />
            <span>
              <span className="font-semibold">CPD accredited certificate</span>
              <span className="block text-xs text-[var(--ink-2)]">Show the CPD badge, provider number #778495 and authorised signature on this course's certificate. On for online courses by default; optional for physical workshops.</span>
            </span>
          </label>

          <div className="border border-[var(--line)] p-3" data-testid="ec-cohorts">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-[var(--ink-2)] flex items-center gap-1.5"><Calendar size={13} /> Workshop / cohort dates</label>
              <label className="text-xs flex items-center gap-1.5 cursor-pointer" title="When on, dates snap to the Monday of that week">
                <input type="checkbox" data-testid="ec-snap-mondays" checked={!!f.snap_mondays} onChange={(e) => set("snap_mondays", e.target.checked)} /> Snap to Mondays
              </label>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
              <input data-testid="ec-newdate" type="date" value={nc.date} onChange={(e) => setNc((p) => ({ ...p, date: e.target.value }))} className={inputCls} />
              <input data-testid="ec-newtime" type="time" value={nc.time} onChange={(e) => setNc((p) => ({ ...p, time: e.target.value }))} className={inputCls} />
              <input data-testid="ec-newlocation" value={nc.location} onChange={(e) => setNc((p) => ({ ...p, location: e.target.value }))} placeholder="Venue / location" className={`${inputCls} col-span-2 sm:col-span-1`} />
              <input data-testid="ec-newcapacity" type="number" min="0" value={nc.capacity} onChange={(e) => setNc((p) => ({ ...p, capacity: e.target.value }))} placeholder="Seats" className={inputCls} />
              <button type="button" onClick={addCohort} data-testid="ec-adddate" className="px-4 border border-[var(--ink)] text-sm font-semibold hover:bg-[var(--ink)] hover:text-white">Add</button>
            </div>
            <div className="space-y-1.5 mt-3">
              {(f.cohorts || []).map((c) => (
                <div key={c.id} className="flex items-center justify-between text-xs bg-[var(--alt)] px-3 py-2" data-testid={`cohort-${c.date}`}>
                  <span><strong>{fmtDay(c.date)}</strong>{c.time ? ` · ${c.time}` : ""}{c.location ? ` · ${c.location}` : ""}{c.capacity ? ` · ${c.capacity} seats` : " · seats: course capacity"}</span>
                  <button type="button" onClick={() => removeCohort(c.id)} data-testid={`remove-cohort-${c.date}`}><X size={13} /></button>
                </div>
              ))}
              {(!f.cohorts || f.cohorts.length === 0) && <span className="text-xs text-[var(--ink-2)]">No dates yet — learners will see the default start date.</span>}
            </div>
            <p className="text-[11px] text-[var(--ink-2)] mt-2">Tip: turn off "Snap to Mondays" for live practical workshops on a specific day (e.g. a Saturday session).</p>
          </div>

          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-[var(--ink-2)]">Marketing copy</label>
            <button type="button" onClick={generateAI} disabled={aiLoading} data-testid="ec-ai-generate" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[var(--ink)] text-white px-3 py-1.5 hover:bg-[var(--accent)] disabled:opacity-60">
              <Sparkles size={13} /> {aiLoading ? "Generating…" : "Generate with AI"}
            </button>
          </div>
          <div><label className="text-xs font-semibold text-[var(--ink-2)]">Who it's for</label><textarea value={f.who_for || ""} onChange={(e) => set("who_for", e.target.value)} rows={2} className={inputCls} /></div>
          <div><label className="text-xs font-semibold text-[var(--ink-2)]">Skills (one per line)</label><textarea data-testid="ec-skills" value={f.skills || ""} onChange={(e) => set("skills", e.target.value)} rows={4} className={inputCls} /></div>
          <div><label className="text-xs font-semibold text-[var(--ink-2)]">Outcomes (one per line)</label><textarea data-testid="ec-outcomes" value={f.outcomes || ""} onChange={(e) => set("outcomes", e.target.value)} rows={4} className={inputCls} /></div>
          <div><label className="text-xs font-semibold text-[var(--ink-2)]">What's included (one per line)</label><textarea value={f.included || ""} onChange={(e) => set("included", e.target.value)} rows={3} className={inputCls} /></div>
          <div><label className="text-xs font-semibold text-[var(--ink-2)]">Requirements (one per line)</label><textarea value={f.requirements || ""} onChange={(e) => set("requirements", e.target.value)} rows={2} className={inputCls} /></div>
          <input value={f.certificate_details || ""} onChange={(e) => set("certificate_details", e.target.value)} className={inputCls} placeholder="Certificate details" />
        </div>
        <DialogFooter><button onClick={save} data-testid="ec-save" className="bg-[var(--accent)] text-white font-semibold px-5 py-2.5 hover:bg-[var(--accent-hover)] w-full">Save changes</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiscountManager({ isAdmin }) {
  const MIN_SPEND = { 5: 60, 10: 140, 25: 250, 50: 350 };
  const [codes, setCodes] = useState([]);
  const [amount, setAmount] = useState(10);
  const [custom, setCustom] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [unlockCode, setUnlockCode] = useState("");
  const load = () => api.get("/provider/discount-codes").then((r) => setCodes(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      await api.post("/provider/discount-codes", { amount: parseFloat(amount), code: custom || null, max_uses: maxUses ? parseInt(maxUses, 10) : null });
      toast.success("Discount code created"); setCustom(""); setMaxUses(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
  };
  const createUnlock = async () => {
    try {
      await api.post("/provider/discount-codes", { kind: "unlock", code: unlockCode || null, max_uses: null });
      toast.success("Free-unlock code created"); setUnlockCode(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
  };
  const toggle = async (c) => { await api.patch(`/provider/discount-codes/${c.id}`, { active: !c.active }); load(); };
  return (
    <div data-testid="discount-manager">
      {isAdmin && (
        <div className="border border-[#1B5E20] bg-white p-5 mb-5" data-testid="unlock-manager">
          <h3 className="font-display font-bold mb-1">Create a free-unlock code (admin only)</h3>
          <p className="text-xs text-[var(--ink-2)] mb-3">A learner who enters this code unlocks the course free of charge. Unlimited uses. Only you can create these.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <input data-testid="uc-code" value={unlockCode} onChange={(e) => setUnlockCode(e.target.value.toUpperCase())} placeholder="Code (e.g. VSSULOC)" className={inputCls} />
            <button onClick={createUnlock} data-testid="uc-create" className="bg-[#1B5E20] text-white font-semibold px-4 py-2 hover:bg-[#154d1a] text-sm">Create unlock code</button>
          </div>
        </div>
      )}
      <div className="border border-[var(--line)] bg-white p-5 mb-5">
        <h3 className="font-display font-bold mb-1">Create a discount code</h3>
        <p className="text-xs text-[var(--ink-2)] mb-3">Minimum spend per code: £5 over £60 · £10 over £140 · £25 over £250 · £50 over £350.</p>
        <div className="grid sm:grid-cols-4 gap-3">
          <select data-testid="dc-amount" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls}>
            {[5, 10, 25, 50].map((a) => <option key={a} value={a}>£{a} off</option>)}
          </select>
          <input data-testid="dc-code" value={custom} onChange={(e) => setCustom(e.target.value.toUpperCase())} placeholder="Custom code (optional)" className={inputCls} />
          <input data-testid="dc-maxuses" type="number" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="Max uses (optional)" className={inputCls} />
          <button onClick={create} data-testid="dc-create" className="bg-[var(--accent)] text-white font-semibold px-4 py-2 hover:bg-[var(--accent-hover)] text-sm">Generate code</button>
        </div>
      </div>
      <div className="border border-[var(--line)] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="discount-table">
          <thead className="bg-[var(--alt)] text-left text-xs uppercase tracking-wide text-[var(--ink-2)]">
            <tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Min spend</th><th className="px-4 py-3">Uses</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {codes.map((c) => (
              <tr key={c.id} className="border-t border-[var(--line)]" data-testid={`discount-row-${c.code}`}>
                <td className="px-4 py-3 font-mono font-semibold">{c.code}</td>
                <td className="px-4 py-3">{c.kind === "unlock" ? <span className="text-[#1B5E20] font-semibold">Free unlock</span> : `£${c.amount} off`}</td>
                <td className="px-4 py-3 text-[var(--ink-2)]">{c.kind === "unlock" ? "—" : `over £${MIN_SPEND[c.amount] ?? "—"}`}</td>
                <td className="px-4 py-3 text-[var(--ink-2)]">{c.uses}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                <td className="px-4 py-3"><span className={`text-[11px] font-semibold px-2 py-0.5 ${c.active ? "bg-[#E8F5E9] text-[#1B5E20]" : "bg-[var(--alt)] text-[var(--ink-2)]"}`}>{c.active ? "Active" : "Inactive"}</span></td>
                <td className="px-4 py-3 text-right"><button onClick={() => toggle(c)} data-testid={`dc-toggle-${c.code}`} className="text-xs font-semibold text-[var(--accent)] hover:underline">{c.active ? "Deactivate" : "Activate"}</button></td>
              </tr>
            ))}
            {codes.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--ink-2)]">No discount codes yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ProviderDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const [courseSearch, setCourseSearch] = useState("");
  const [learnerSearch, setLearnerSearch] = useState("");

  const featuredCount = courses.filter((c) => c.featured).length;

  const toggleFeatured = async (c) => {
    try {
      await api.patch(`/courses/${c.id}/featured`, { featured: !c.featured });
      toast.success(c.featured ? "Removed from home page" : "Promoted to home page");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const load = useCallback(async () => {
    try {
      const [s, c, e] = await Promise.all([
        api.get("/provider/stats"), api.get("/courses"), api.get("/provider/enrolments"),
      ]);
      setStats(s.data); setCourses(c.data); setEnrolments(e.data);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error("Could not load dashboard data.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10" data-testid="provider-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs tracking-label uppercase font-semibold text-[var(--accent)]">Operations</p>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mt-1">Provider dashboard</h1>
        </div>
        {user?.role !== "tutor" && <CreateCourseDialog onCreated={load} />}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Stat icon={PoundSterling} label="Revenue collected" value={stats ? GBP(stats.total_revenue) : "…"} />
        <Stat icon={Users} label="Enrolments" value={stats?.total_enrolments ?? "…"} />
        <Stat icon={BookOpen} label="Courses" value={stats?.total_courses ?? "…"} />
        <Stat icon={Award} label="Completed" value={stats?.completed ?? "…"} />
        <Stat icon={TrendingUp} label="Completion rate" value={stats != null ? `${stats.completion_rate ?? 0}%` : "…"} />
      </div>

      <Tabs defaultValue="learners">
        <TabsList className="rounded-none bg-[var(--alt)]">
          <TabsTrigger value="learners" data-testid="tab-learners" className="rounded-none data-[state=active]:bg-white">Learners & applications</TabsTrigger>
          <TabsTrigger value="courses" data-testid="tab-courses" className="rounded-none data-[state=active]:bg-white">Courses</TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance" className="rounded-none data-[state=active]:bg-white">Finance</TabsTrigger>
          <TabsTrigger value="discounts" data-testid="tab-discounts" className="rounded-none data-[state=active]:bg-white">Discounts</TabsTrigger>
          <TabsTrigger value="store" data-testid="tab-store" className="rounded-none data-[state=active]:bg-white">Store</TabsTrigger>
          <TabsTrigger value="homepage" data-testid="tab-homepage" className="rounded-none data-[state=active]:bg-white">Homepage</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="rounded-none data-[state=active]:bg-white">Enquiries</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services" className="rounded-none data-[state=active]:bg-white">Services</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails" className="rounded-none data-[state=active]:bg-white">Emails</TabsTrigger>
        </TabsList>

        <TabsContent value="learners" className="mt-5">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <input data-testid="learners-search" value={learnerSearch} onChange={(e) => setLearnerSearch(e.target.value)} placeholder="Search by name, email or course…" className="px-3 py-2 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm w-full sm:w-80" />
            <span className="text-sm text-[var(--ink-2)]">{enrolments.length} applications</span>
          </div>
          <div className="border border-[var(--line)] bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="learners-table">
              <thead className="bg-[var(--alt)] text-left text-xs uppercase tracking-wide text-[var(--ink-2)]">
                <tr><th className="px-4 py-3">Learner</th><th className="px-4 py-3">Course</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Paid</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {enrolments.filter((e) => {
                  const q = learnerSearch.trim().toLowerCase();
                  if (!q) return true;
                  return [e.learner_name, e.learner_email, e.course_title].some((v) => (v || "").toLowerCase().includes(q));
                }).map((e) => (
                  <tr key={e.id} className="border-t border-[var(--line)]" data-testid={`learner-row-${e.id}`}>
                    <td className="px-4 py-3"><div className="font-semibold">{e.learner_name}</div><div className="text-xs text-[var(--ink-2)]">{e.learner_email}</div></td>
                    <td className="px-4 py-3">{e.course_title}</td>
                    <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                    <td className="px-4 py-3"><PaymentBadge status={e.payment_status} /></td>
                    <td className="px-4 py-3">{GBP(e.amount_paid)} / {GBP(e.total_price)}</td>
                    <td className="px-4 py-3 text-right"><ManageEnrolmentDialog enrolment={e} onChange={load} isAdmin={user?.role === "platform_admin"} /></td>
                  </tr>
                ))}
                {enrolments.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--ink-2)]">No applications yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="courses" className="mt-5">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <input data-testid="pcourse-search" value={courseSearch} onChange={(e) => setCourseSearch(e.target.value)} placeholder="Search your courses…" className="px-3 py-2 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm w-full sm:w-72" />
            <span className="text-sm text-[var(--ink-2)]" data-testid="featured-count">{featuredCount}/4 promoted on home page · {courses.length} courses</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.filter((c) => c.title.toLowerCase().includes(courseSearch.toLowerCase()) || c.category.toLowerCase().includes(courseSearch.toLowerCase())).map((c) => (
              <div key={c.id} className="border border-[var(--line)] bg-white relative" data-testid={`pcourse-${c.id}`}>
                <img src={c.image} alt="" className="w-full aspect-[16/9] object-cover" />
                <button onClick={() => toggleFeatured(c)} data-testid={`feature-toggle-${c.id}`}
                  className={`absolute top-2 right-2 grid place-items-center h-9 w-9 transition-colors ${c.featured ? "bg-[var(--accent)] text-white" : "bg-white/90 text-[var(--ink)] hover:bg-white"}`}
                  title={c.featured ? "Featured on home page" : "Promote to home page"}>
                  <Star size={16} fill={c.featured ? "currentColor" : "none"} />
                </button>
                <div className="p-4">
                  <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide flex items-center gap-2">{c.category}{c.course_ref && <span className="text-[var(--ink-2)]">· {c.course_ref}</span>}</div>
                  <h3 className="font-display font-bold leading-tight">{c.title}</h3>
                  <div className="flex justify-between text-sm mt-2"><span className="font-semibold">{GBP(c.price)}</span><span className="text-[var(--ink-2)]">{c.places_remaining}/{c.capacity} places</span></div>
                  <div className="mt-3"><EditCourseDialog course={c} onSaved={load} /></div>
                  <CurriculumManager course={c} />
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="finance" className="mt-5">
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <Stat icon={PoundSterling} label="Total revenue" value={stats ? GBP(stats.total_revenue) : "…"} />
            <Stat icon={PoundSterling} label="Outstanding" value={stats ? GBP(stats.outstanding) : "…"} />
            <Stat icon={PoundSterling} label="Deposits collected" value={stats ? GBP(stats.deposits_collected) : "…"} />
          </div>
          <div className="border border-[var(--line)] bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="finance-table">
              <thead className="bg-[var(--alt)] text-left text-xs uppercase tracking-wide text-[var(--ink-2)]"><tr><th className="px-4 py-3">Course</th><th className="px-4 py-3">Enrolments</th><th className="px-4 py-3">Completed</th><th className="px-4 py-3">Revenue</th></tr></thead>
              <tbody>
                {stats?.by_course?.map((r) => (
                  <tr key={r.course} className="border-t border-[var(--line)]"><td className="px-4 py-3">{r.course}</td><td className="px-4 py-3">{r.enrolments}</td><td className="px-4 py-3">{r.completed ?? 0}</td><td className="px-4 py-3 font-semibold">{GBP(r.revenue)}</td></tr>
                ))}
                {(!stats?.by_course?.length) && <tr><td colSpan={4} className="px-4 py-10 text-center text-[var(--ink-2)]">No revenue yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="emails" className="mt-5">
          <EmailAdmin />
        </TabsContent>

        <TabsContent value="discounts" className="mt-5">
          <DiscountManager isAdmin={user?.role === "platform_admin"} />
        </TabsContent>

        <TabsContent value="store" className="mt-5">
          <StoreManager />
        </TabsContent>

        <TabsContent value="homepage" className="mt-5">
          <ContentManager />
        </TabsContent>

        <TabsContent value="enquiries" className="mt-5">
          <EnquiriesManager />
        </TabsContent>

        <TabsContent value="services" className="mt-5">
          <ServicesManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}
